import React, { useEffect, useState } from 'react';
import ShareButton from './ShareButton.js';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import '../../css/ProjectCard.css';
import LinkifyText from './LinkifyText.js';
import { Link } from "react-router-dom";
import { API_BASE_URL } from '../scripts/control';
import { ROUTES, ROUTE_PATHS } from '../../../config/constants';
import ImageCrop from "../components/ImageCrop";
import RichTextInput from '../components/RichTextInput';
import { linkifyContent } from '../../../utils/linkifyHtml';
import { dataURLtoBlob } from '../../../utils/dataURLtoBlob';
import OptimizedImage from '../../../components/OptimizedImage';

const ProjectCard = () => {
    const [projects, setProjects] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [deletingProject, setDeletingProject] = useState(null);
    const { isAdmin } = useAdminAuth();

    // Estado para la paginación
    const [currentPage, setCurrentPage] = useState(1);
    const projectsPerPage = 5;
    const pageLimit = 5; // Páginas visibles a la vez

    const [showCropModal, setShowCropModal] = useState(false); // modal de crop
    const [selectedImage, setSelectedImage] = useState(null);  // imagen original (DataURL)
    const [croppedImage, setCroppedImage] = useState(null); 
    const [bodyContent, setBodyContent] = useState('');
    const [locationContent, setLocationContent] = useState('');

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/project_func.php`)
            .then(response => response.json())
            .then(data => {
                if (data.error) {
                } else {
                    setProjects(data);
                    setExpanded(data.reduce((acc, project) => ({ ...acc, [project.idproject]: false }), {}));
                }
            })
            .catch(() => {});
    }, []);

    const handleToggle = (id) => {
        setExpanded(prevState => ({ ...prevState, [id]: !prevState[id] }));
    };

    const handleEdit = (project) => {
        setEditingProject(project);
        setBodyContent(project.body || '');
        setLocationContent(project.location || '');
        setSelectedImage(null);
        setCroppedImage(null);
    };

    const handleDelete = (project) => {
        setDeletingProject(project);
    };

    const confirmDelete = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/delete_project.php`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: deletingProject.idproject }),
            });

            if (response.ok) {
                setProjects(projects.filter(project => project.idproject !== deletingProject.idproject));
                setDeletingProject(null);
            } else {
            }
        } catch (error) {
        }
    };

    const cancelDelete = () => {
        setDeletingProject(null);
    };

    const handleCloseModal = () => {
        setShowConfirmCloseModal(true);
    };

    const handleConfirmClose = () => {
        setEditingProject(null);
        setShowConfirmCloseModal(false);
        setSelectedImage(null);
        setCroppedImage(null);
    };

    const handleCancelClose = () => {
        setShowConfirmCloseModal(false);
    };

    const handleSave = async () => {
        const formData = new FormData();
        const title = document.getElementById('title').value;
        const body = bodyContent;
        const imgFile = document.getElementById('img_url').files[0];
        const imgAlt = document.getElementById('img_alt').value;
        const link = document.getElementById('link').value;
        const day = document.getElementById('day').value;
        const hour = document.getElementById('hour').value;
        const location = locationContent;

        formData.append('id', editingProject.idproject);
        formData.append('title', title);
        formData.append('body', body);
        formData.append('day', day);
        formData.append('hour', hour);
        formData.append('location', location);
        formData.append('img_alt', imgAlt);
        formData.append('link', link);
        let savedImgFilename = editingProject.img_url;
        if (croppedImage) {
          const { blob, ext, isValid } = dataURLtoBlob(croppedImage);
          const filename = imgFile?.name || `img_${Date.now()}.${ext}`;
          if (isValid && blob.size > 0) {
            formData.append('img_url', blob, filename);
            savedImgFilename = filename;
          } else {
            console.warn('Conversión de imagen recortada falló, usando archivo original');
            const file = document.getElementById('img_url').files[0];
            if (file) {
              formData.append('img_url', file);
              savedImgFilename = file.name;
            }
          }
        } else {
          const file = document.getElementById('img_url').files[0];
          if (file) {
            formData.append('img_url', file);
            savedImgFilename = file.name;
          }
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/update_project.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const data = await response.json().catch(() => ({}));
            if (response.ok && data.success) {
                const finalImgUrl = data.img_url != null ? data.img_url : savedImgFilename;
                const updatedProjects = projects.map(project =>
                    project.idproject === editingProject.idproject
                        ? {
                              ...project,
                              title,
                              body,
                              day,
                              hour,
                              location,
                              img_url: finalImgUrl,
                              imgAlt,
                              link,
                          }
                        : project
                );
                setProjects(updatedProjects);
                setEditingProject(null);
                setSelectedImage(null);
                setCroppedImage(null);
            } else {
                alert('Error al actualizar el proyecto.');
            }
        } catch (error) {
            alert('Error al actualizar el proyecto: ' + (error?.message || 'Error desconocido'));
        }
    };

    const formatDate = (dateString) => {
        const options = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        return new Date(dateString).toLocaleString('es-ES', options);
    };

    // Crear URL para agregar evento a Google Calendar
    const createGoogleCalendarUrl = (project) => {
        const formatDateForGoogle = (dateString, timeString) => {
            if (!dateString || !timeString) {
                return null;
            }

            const dateTimeString = `${dateString}T${timeString}`;  // Formato: YYYY-MM-DDTHH:MM:SS
            const parsedDate = new Date(dateTimeString);

            if (isNaN(parsedDate.getTime())) {
                return null;
            }

            return parsedDate.toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';
        };

        const startDate = formatDateForGoogle(project.day, project.hour);
        const location = project.location || 'Sin ubicación especificada';

        if (!startDate) {
            return '#';
        }

        return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
            project.title
        )}&dates=${startDate}/${startDate}&location=${encodeURIComponent(location)}&sf=true&output=xml`;
    };

    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.slice(0, maxLength) + '...';
        }
        return text;
    };

    const indexOfLastProject = currentPage * projectsPerPage;
    const indexOfFirstProject = indexOfLastProject - projectsPerPage;
    const currentProjects = projects.slice(indexOfFirstProject, indexOfLastProject);

    const totalPages = Math.ceil(projects.length / projectsPerPage);

    const startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    const endPage = Math.min(totalPages, startPage + pageLimit - 1);
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // ------------------- Manejo de imagen en la edición (crop) -------------------
    const handleEditImageChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
    
      // Convertimos en DataURL
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result); // guardamos la imagen original
        setShowCropModal(true);         // abrimos el modal de recorte
      };
      reader.readAsDataURL(file);
    };
    
    const handleCropSave = (cropped) => {
      setCroppedImage(cropped);
      setShowCropModal(false);
    };

    return (
        <div className="container-fluid mb-4">
            <div className="row">
                {currentProjects.map((project) => {
                    const imgSrc = project.img_url
                        ? `${API_BASE_URL}/public/uploads/images/${project.img_url}`
                        : `${API_BASE_URL}/public/uploads/images/comic.jpg`;


                return (
                    <div className="col-md-4 mb-4" key={project.idproject}>
                    <div
                        className={`card ${expanded[project.idproject] ? 'expanded' : ''}`}
                        id={`card-${project.idproject}`}
                    >
                        <h5 className="card-title">{project.title}</h5>
                        <OptimizedImage src={imgSrc} alt={project.img_alt || 'Imagen por defecto'} loading="lazy" aspectRatio={16/9} />
                        <div className="card-body ms-1">
                            <small className="text-muted mt-3">Última actualización: {new Date(project.date).toLocaleDateString()}</small><br />
                            <div
                              className="article-preview"
                              dangerouslySetInnerHTML={{
                               __html: linkifyContent(project.body)
                              }}
                            /><br />

                            {project.link && 
                            <Link to={`${project.link}`} target='blank'>
                                <button
                                        className="btn btn-primary"
                                        style={{
                                            marginRight: '10px',
                                            marginBottom: '10px',
                                            padding: '5px 10px',
                                            backgroundColor: '#005EC4',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Ir al proyecto
                                </button>
                            </Link>
                            }
                            <Link to={ROUTE_PATHS.DETAIL_LINK.PROJECT(project.idproject)}>
                                <button
                                        style={{
                                            marginRight: '10px',
                                            marginBottom: '10px',
                                            padding: '5px 10px',
                                            backgroundColor: '#005EC4',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Ver detalle
                                </button>
                            </Link>
                            {project.location && project.day && project.hour &&(<a
                                href={createGoogleCalendarUrl(project)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-calendar">
                                <i className="fa-brands fa-google"></i> Agendar
                            </a>)}
                            {isAdmin && (
                                <div style={{ marginTop: '10px' }}>
                                    <button
                                        onClick={() => handleEdit(project)}
                                        style={{
                                            marginRight: '10px',
                                            padding: '5px 10px',
                                            backgroundColor: '#ffc107',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDelete(project)}
                                        style={{
                                            padding: '5px 10px',
                                            backgroundColor: '#dc3545',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            color: 'white',
                                        }}
                                    >
                                        Borrar
                                    </button>
                                </div>
                            )}
                        </div>
                        <ShareButton
                            shareUrl={`${API_BASE_URL}/detalles/proyecto/${project.idproject}`}
                            title={`Mira este proyecto: ${project.title}`}
                        />
                        <br />
                         <hr />
                        </div>
                    </div>
                );
            })}
        </div>

            {/* Paginación */}
            {projects.length > projectsPerPage && (
                <div className="pagination">
                    <button
                        className="back"
                        onClick={prevPage}
                        disabled={currentPage === 1}
                    >
                        Anterior
                    </button>

                    {pageNumbers.map((pageNumber) => (
                        <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
                            style={{
                                backgroundColor: currentPage === pageNumber ? '#007bff' : '',
                                color: currentPage === pageNumber ? 'white' : '',
                            }}
                        >
                            {pageNumber}
                        </button>
                    ))}

                    <button
                        className="next"
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                    >
                        Siguiente
                    </button>
                </div>
            )}

            {/* Modal para editar el proyecto */}
            {editingProject && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Editar proyecto</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCloseModal}></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <label htmlFor="title">Título del proyecto:</label><br />
                                    <input type="text" id="title" name="title" defaultValue={editingProject.title} /><br />
                                    <span id="body-label-project-edit">Texto principal:</span><br />
                                    <RichTextInput
                                        id="body-project-edit"
                                        ariaLabelledBy="body-label-project-edit"
                                        initialContent={bodyContent}
                                        onChange={(content) => setBodyContent(content)}
                                        placeholder="Pega o escribe aquí el contenido con formato..."
                                        style={{ minHeight: '150px', direction: 'ltr' }}
                                    />
                                    <label htmlFor="img_url">Imagen del proyecto:</label><br />
                                    <input
                                      type="file"
                                      id="img_url"
                                      name="img_url"
                                      accept="image/*"
                                      onChange={handleEditImageChange}
                                    />
                                    {/* Previsualización de la imagen */}
                                    {croppedImage ? (
                                      <div>
                                        <p>Previsualización (Imagen recortada):</p>
                                        <img
                                          src={croppedImage}
                                          alt="Imagen recortada"
                                          style={{ maxWidth: '100%' }}
                                        />
                                      </div>
                                    ) : selectedImage ? (
                                      <div>
                                        <p>Previsualización (Sin recortar):</p>
                                        <img
                                          src={selectedImage}
                                          alt="Imagen seleccionada"
                                          style={{ maxWidth: '100%' }}
                                        />
                                      </div>
                                    ) : null}
                                    <br />
                                    <label htmlFor="img_alt">Texto alternativo de la imagen:</label><br />
                                    <input type="text" id="img_alt" name="img_alt" defaultValue={editingProject.img_alt} /><br />
                                    <small>necesario para personas con discapacidad visual</small>
                                    <label htmlFor="link">Enlace al proyecto:</label><br />
                                    <input type="text" id="link" name="link" defaultValue={editingProject.link} /><br />
                                    <hr />
                                    <small>Rellenar a continuación para permitir agendar con Google Calendar:</small><br />
                                    <label htmlFor="day">Día del evento:</label><br />
                                    <small className="text-muted"> Se ordenará por este parámetro</small><br />
                                    <input type="date" id="day" name="day" defaultValue={editingProject.day} /><br />
                                    <label htmlFor="hour">Hora de inicio del evento:</label><br />
                                    <input type="time" id="hour" name="hour" defaultValue={editingProject.hour} /><br /><br />
                                    <label htmlFor="location">Lugar del evento:</label><br />
                                    <small>Es importante incluir aquí detalles como número de la calle y cp, para que google maps pueda localizarlo.</small><br />
                                    <RichTextInput id="location" initialContent={locationContent} onChange={setLocationContent} placeholder="Lugar..." editorKey={`project-edit-${editingProject?.idproject}-loc`} style={{ minHeight: '60px' }} /><br />  
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={handleSave}>Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de cierre */}
            {showConfirmCloseModal && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar cierre</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCancelClose}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Quieres abandonar la edición? Se perderán los cambios no guardados.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCancelClose}>Volver</button>
                                <button type="button" className="btn btn-primary" onClick={handleConfirmClose}>Salir</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de borrado */}
            {deletingProject && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar borrado</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Estás seguro de que deseas borrar el proyecto {deletingProject.title}?</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>Cancelar</button>
                                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Borrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal de crop (se abre cuando subimos una imagen en la edición) */}
            {showCropModal && (
              <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog" style={{ maxWidth: '90%' }}>
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Recortar imagen</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowCropModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <ImageCrop image={selectedImage} onImageSave={handleCropSave} />
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
    );
};

export default ProjectCard;
